import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import Navbar from "../../Navbar";
import ModalAnularOrdenPago from "./ModalAnularOrdenPago";
import EditorLoteOrdenPago from "./EditorLoteOrdenPago";
import {
  anularLoteOrdenPago,
  aprobarLoteOrdenPago,
  getLoteOrdenPagoById,
} from "./ApiServices";
import {
  colorEstadoLote,
  colorEstadoOp,
  esLoteAnulable,
  esLoteAprobable,
  esOpAnulada,
  esOpConciliada,
  esOpEditable,
  esOpGenerada,
  etiquetaEstadoLote,
  etiquetaEstadoOp,
  etiquetaTipoMedio,
  etiquetaComprobanteCorta,
  formatCuit,
  formatMoneda,
  lineasRetencionesLote,
  mapDetalleLoteResponse,
  tooltipTipoComprobante,
} from "./utils";

const formatearFecha = (valor) => {
  if (!valor) return "—";
  const m = moment(valor);
  return m.isValid() ? m.format("DD/MM/YYYY") : "—";
};

/** OP editable dentro del lote (mismo criterio que el paso 3 del wizard). */
const opEsEditableEnLote = (op, loteEstado) => {
  if (esOpEditable(op?.estado)) return true;
  // Si el payload del lote no trae estado de la OP, inferir por el lote.
  if (!op?.estado && esLoteAprobable(loteEstado)) return true;
  if (
    esLoteAprobable(loteEstado) &&
    !esOpGenerada(op?.estado) &&
    !esOpConciliada(op?.estado) &&
    !esOpAnulada(op?.estado)
  ) {
    return true;
  }
  return false;
};

const TarjetaTotal = ({ titulo, valor, detalle, destacado = false }) => (
  <Paper
    elevation={destacado ? 3 : 1}
    sx={{
      px: 1.5,
      py: 1,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      backgroundColor: destacado ? "primary.main" : "#fff",
      color: destacado ? "primary.contrastText" : undefined,
    }}
  >
    <Typography
      variant="caption"
      lineHeight={1.2}
      sx={{ opacity: destacado ? 0.9 : undefined }}
      color={destacado ? "inherit" : "text.secondary"}
    >
      {titulo}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
      {valor}
    </Typography>
    {detalle ? (
      <Typography
        variant="caption"
        lineHeight={1.2}
        color={destacado ? "inherit" : "text.secondary"}
        sx={{ opacity: destacado ? 0.9 : undefined }}
      >
        {detalle}
      </Typography>
    ) : null}
  </Paper>
);

const TablaComprobantesProveedor = ({ comprobantes = [] }) => {
  if (!comprobantes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Sin comprobantes en esta orden.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#2471A3" }}>
            {["Comprobante", "Fecha", "Vencimiento", "Total", "A pagar"].map(
              (h) => (
                <TableCell
                  key={h}
                  align={h === "Total" || h === "A pagar" ? "right" : "left"}
                  sx={{ color: "#fff", fontWeight: 500, whiteSpace: "nowrap" }}
                >
                  {h}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {comprobantes.map((comp, index) => (
            <TableRow key={comp.idcomprobante ?? `comp-${index}`} hover>
              <TableCell>
                <Tooltip title={tooltipTipoComprobante(comp) || ""}>
                  <span>{etiquetaComprobanteCorta(comp)}</span>
                </Tooltip>
              </TableCell>
              <TableCell>{formatearFecha(comp.fecha_factura)}</TableCell>
              <TableCell>{formatearFecha(comp.fecha_vencimiento)}</TableCell>
              <TableCell align="right">
                {formatMoneda(comp.total ?? comp.total_comprobante)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatMoneda(comp.importe_a_pagar ?? comp.importe)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MediosPagoProveedor = ({ medios = [] }) => {
  if (!medios.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Sin medios de pago (las retenciones absorben el importe).
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {medios.map((medio, index) => {
        const cuentas = Array.isArray(medio?.cuentas) ? medio.cuentas : [];
        return (
          <Box key={`${medio?.tipo ?? "medio"}-${index}`}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              spacing={1}
            >
              <Typography variant="body2" fontWeight={600}>
                {etiquetaTipoMedio(medio?.tipo, medio)}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                {formatMoneda(medio?.importe)}
              </Typography>
            </Stack>
            {cuentas.map((cuenta, i) => (
              <Stack
                key={`${cuenta?.id_bancos_cuentas ?? i}`}
                direction="row"
                justifyContent="space-between"
                spacing={1}
                sx={{ pl: 1.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {cuenta?.banco_nombre ??
                    cuenta?.banco ??
                    `Banco ${cuenta?.id_banco ?? "—"}`}
                  {cuenta?.nro_cuenta ? ` · ${cuenta.nro_cuenta}` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatMoneda(cuenta?.importe)}
                </Typography>
              </Stack>
            ))}
          </Box>
        );
      })}
    </Stack>
  );
};

const DialogAprobarLote = ({
  open,
  onClose,
  onConfirm,
  aprobando,
  nroLote,
  cantidadOrdenes,
  cantidadPagosVarios = 0,
  totalNeto,
}) => {
  let mensajeAprobacion =
    "Se aprueba el lote y pasa a estado Generada / Aprobado.";
  if (cantidadOrdenes > 0 && cantidadPagosVarios > 0) {
    mensajeAprobacion = `Se aprueban las ${cantidadOrdenes} órdenes de pago y ${cantidadPagosVarios} pago${cantidadPagosVarios === 1 ? "" : "s"} varios del lote.`;
  } else if (cantidadOrdenes > 0) {
    mensajeAprobacion = `Se aprueban juntas las ${cantidadOrdenes} órdenes de pago del lote y pasan a estado Generada.`;
  } else if (cantidadPagosVarios > 0) {
    mensajeAprobacion = `Se aprueban los ${cantidadPagosVarios} pago${cantidadPagosVarios === 1 ? "" : "s"} varios del lote.`;
  }

  return (
  <Dialog open={open} onClose={aprobando ? undefined : onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>
      Aprobar lote{nroLote ? ` · ${nroLote}` : ""}
    </DialogTitle>
    <DialogContent>
      <Alert severity="info" sx={{ mb: 2 }}>
        {mensajeAprobacion}
      </Alert>
      <Typography variant="body2" color="text.secondary">
        Total neto a pagar
      </Typography>
      <Typography variant="h6" fontWeight={700} color="primary.main">
        {formatMoneda(totalNeto)}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={aprobando}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={aprobando}
        startIcon={
          aprobando ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <CheckCircleOutlineIcon />
          )
        }
      >
        {aprobando ? "Aprobando…" : "Aprobar lote"}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

/**
 * Pantalla de revisión del lote: proveedores, comprobantes, montos,
 * retenciones y medios de pago consolidados, con aprobación en un clic.
 */
export default function DetalleLoteOrdenPago() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [dialogAprobar, setDialogAprobar] = useState(false);
  const [aprobando, setAprobando] = useState(false);
  const [modalAnular, setModalAnular] = useState(false);
  const [anulando, setAnulando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLoteOrdenPagoById(id);
      setDetalle(mapDetalleLoteResponse(data));
    } catch (err) {
      setError(err.message || "Error al cargar el lote");
      setDetalle(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const lote = detalle?.lote ?? {};
  const proveedores = useMemo(() => detalle?.proveedores ?? [], [detalle]);
  const pagosNoProveedor = useMemo(
    () => detalle?.pagos_no_proveedor ?? [],
    [detalle]
  );
  const totales = detalle?.totales ?? null;
  const totalesPorMedio = detalle?.totalesPorMedio ?? [];
  const loteSoloPagosVarios =
    proveedores.length === 0 && pagosNoProveedor.length > 0;

  const puedeAprobar = esLoteAprobable(lote.estado);
  const puedeAnular = esLoteAnulable(lote.estado);

  const proveedoresEditables = useMemo(
    () =>
      proveedores.filter((prov) => {
        const op = prov.ordenPago ?? {};
        const opId = op.id ?? op.id_orden_pago ?? null;
        return Boolean(opId) && opEsEditableEnLote(op, lote.estado);
      }),
    [proveedores, lote.estado]
  );

  const proveedoresSoloLectura = useMemo(
    () =>
      proveedores.filter((prov) => {
        const op = prov.ordenPago ?? {};
        const opId = op.id ?? op.id_orden_pago ?? null;
        return !(Boolean(opId) && opEsEditableEnLote(op, lote.estado));
      }),
    [proveedores, lote.estado]
  );

  const modoEdicionLote = proveedoresEditables.length > 0;

  const handleAprobar = async () => {
    setAprobando(true);
    try {
      const data = await aprobarLoteOrdenPago(id, {
        user: localStorage.getItem("user"),
      });
      setDialogAprobar(false);
      await swal({
        title: "Lote aprobado",
        text:
          data?.msg ||
          `Se aprobaron las ${proveedores.length} órdenes de pago del lote.`,
        icon: "success",
        closeOnClickOutside: false,
      });
      await cargar();
    } catch (err) {
      swal({
        title: "No se pudo aprobar el lote",
        text: err.message || "No se pudo aprobar el lote",
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setAprobando(false);
    }
  };

  const handleAnular = async (motivo) => {
    setAnulando(true);
    try {
      const data = await anularLoteOrdenPago(id, {
        user: localStorage.getItem("user"),
        motivo,
      });
      setModalAnular(false);
      await swal({
        title: "Lote anulado",
        text:
          data?.msg ||
          "Se anularon las órdenes del lote y los comprobantes quedaron disponibles otra vez.",
        icon: "success",
        closeOnClickOutside: false,
      });
      await cargar();
    } catch (err) {
      swal({
        title: "No se pudo anular el lote",
        text: err.message || "No se pudo anular el lote",
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setAnulando(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/ordenes_pago_proveedores")}
          >
            Volver al listado
          </Button>
          {lote.estado ? (
            <Chip
              label={etiquetaEstadoLote(lote.estado, lote.estado_label)}
              size="small"
              color={colorEstadoLote(lote.estado)}
              variant="outlined"
            />
          ) : null}
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Revisión de lote{lote.nro_lote ? ` · ${lote.nro_lote}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {loteSoloPagosVarios
                ? `${pagosNoProveedor.length} pago${
                    pagosNoProveedor.length === 1 ? "" : "s"
                  } varios / impuestos y conceptos`
                : `${proveedores.length} proveedor${
                    proveedores.length === 1 ? "" : "es"
                  } · una orden de pago por CUIT${
                    pagosNoProveedor.length
                      ? ` · ${pagosNoProveedor.length} pago${
                          pagosNoProveedor.length === 1 ? "" : "s"
                        } varios`
                      : ""
                  }`}
            </Typography>
          </Box>
        </Stack>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : detalle ? (
          <>
            <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Fecha de pago
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatearFecha(lote.fecha_pago ?? lote.fecha)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Generado por
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {lote.user || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Alta
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {lote.created_at
                      ? moment(lote.created_at).format("DD/MM/YYYY HH:mm")
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Empresa
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {lote.empresa?.nombre ?? lote.empresa?.razon_social ?? "—"}
                  </Typography>
                </Grid>
                {lote.observaciones ? (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Observaciones
                    </Typography>
                    <Typography variant="body2">{lote.observaciones}</Typography>
                  </Grid>
                ) : null}
                {lote.motivo_anulacion ? (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      Lote anulado: {lote.motivo_anulacion}
                    </Alert>
                  </Grid>
                ) : null}
              </Grid>
            </Paper>

            {modoEdicionLote ? (
              <>
                <EditorLoteOrdenPago
                  proveedoresLote={proveedoresEditables}
                  editable
                  onGuardado={cargar}
                />

                {proveedoresSoloLectura.length > 0 ? (
                  <Box sx={{ mt: 3 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <ReceiptLongIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={700}>
                        Órdenes ya cerradas en el lote
                      </Typography>
                    </Stack>
                    {proveedoresSoloLectura.map((prov) => {
                      const op = prov.ordenPago ?? {};
                      const opId = op.id ?? op.id_orden_pago ?? null;
                      const retenciones = lineasRetencionesLote(prov.retenciones);
                      const claveAcc = String(
                        prov.cuit || opId || prov.indice
                      );
                      return (
                        <Accordion key={claveAcc} defaultExpanded={false} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} md={5}>
                                <Typography variant="body2" fontWeight={700}>
                                  {prov.razon_social || "Proveedor"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatCuit(prov.cuit)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {op.nro_op ? `OP ${op.nro_op}` : "OP —"}
                                  </Typography>
                                  {op.estado ? (
                                    <Chip
                                      label={etiquetaEstadoOp(
                                        op.estado,
                                        op.estado_label
                                      )}
                                      size="small"
                                      color={colorEstadoOp(op.estado)}
                                      variant="outlined"
                                    />
                                  ) : null}
                                </Stack>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Neto
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color="primary.main"
                                >
                                  {formatMoneda(prov.totales?.neto)}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                md={1}
                                sx={{ textAlign: { md: "right" } }}
                              >
                                {opId ? (
                                  <Tooltip title="Abrir ficha de la orden de pago">
                                    <Button
                                      size="small"
                                      startIcon={
                                        <OpenInNewIcon fontSize="small" />
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/ordenes_pago_proveedores/${opId}`
                                        );
                                      }}
                                    >
                                      OP
                                    </Button>
                                  </Tooltip>
                                ) : null}
                              </Grid>
                            </Grid>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TablaComprobantesProveedor
                              comprobantes={prov.comprobantes}
                            />
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  gutterBottom
                                >
                                  Retenciones
                                </Typography>
                                {retenciones.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Sin retenciones.
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.25}>
                                    {retenciones.map((linea) => (
                                      <Stack
                                        key={linea.key}
                                        direction="row"
                                        justifyContent="space-between"
                                        spacing={1}
                                      >
                                        <Typography variant="body2">
                                          {linea.label}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                        >
                                          {formatMoneda(linea.importe)}
                                        </Typography>
                                      </Stack>
                                    ))}
                                  </Stack>
                                )}
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  gutterBottom
                                >
                                  Medios de pago
                                </Typography>
                                <MediosPagoProveedor medios={prov.mediosPago} />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                ) : null}
              </>
            ) : (
              <>
                <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
                  <Grid item xs={12} sm={6} md={3}>
                    <TarjetaTotal
                      titulo="Comprobantes"
                      valor={formatMoneda(totales?.comprobantes)}
                      detalle={
                        totales?.cantidad_comprobantes
                          ? `${totales.cantidad_comprobantes} comprobante${
                              totales.cantidad_comprobantes === 1 ? "" : "s"
                            }`
                          : null
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TarjetaTotal
                      titulo="Retenciones"
                      valor={formatMoneda(totales?.retenciones)}
                      detalle="Certificados por CUIT"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TarjetaTotal
                      titulo="Neto del lote"
                      valor={formatMoneda(totales?.neto)}
                      detalle={
                        loteSoloPagosVarios
                          ? `${pagosNoProveedor.length} pago${
                              pagosNoProveedor.length === 1 ? "" : "s"
                            } varios`
                          : `${proveedores.length} orden${
                              proveedores.length === 1 ? "" : "es"
                            } de pago${
                              pagosNoProveedor.length
                                ? ` · ${pagosNoProveedor.length} varios`
                                : ""
                            }`
                      }
                      destacado
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={1}
                      sx={{
                        px: 1.5,
                        py: 1,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ mb: 0.25 }}
                      >
                        <PaymentsOutlinedIcon
                          color="primary"
                          sx={{ fontSize: 16 }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          lineHeight={1.2}
                        >
                          Montos por medio de pago
                        </Typography>
                      </Stack>
                      {totalesPorMedio.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Stack spacing={0}>
                          {totalesPorMedio.map((medio) => (
                            <Stack
                              key={medio.tipo}
                              direction="row"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Typography variant="body2" lineHeight={1.35}>
                                {medio.label}
                                {medio.cantidad ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    ({medio.cantidad})
                                  </Typography>
                                ) : null}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                lineHeight={1.35}
                              >
                                {formatMoneda(medio.importe)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <ReceiptLongIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Proveedores del lote
                  </Typography>
                </Stack>

                {proveedores.map((prov) => {
                  const op = prov.ordenPago ?? {};
                  const opId = op.id ?? op.id_orden_pago ?? null;
                  const retenciones = lineasRetencionesLote(prov.retenciones);
                  const claveAcc = String(prov.cuit || opId || prov.indice);
                  return (
                    <Accordion
                      key={claveAcc}
                      defaultExpanded={proveedores.length <= 2}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={5}>
                            <Typography variant="body2" fontWeight={700}>
                              {prov.razon_social || "Proveedor"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatCuit(prov.cuit)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {op.nro_op ? `OP ${op.nro_op}` : "OP —"}
                              </Typography>
                              {op.estado ? (
                                <Chip
                                  label={etiquetaEstadoOp(
                                    op.estado,
                                    op.estado_label
                                  )}
                                  size="small"
                                  color={colorEstadoOp(op.estado)}
                                  variant="outlined"
                                />
                              ) : null}
                            </Stack>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Neto
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="primary.main"
                            >
                              {formatMoneda(prov.totales?.neto)}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={1}
                            sx={{ textAlign: { md: "right" } }}
                          >
                            {opId ? (
                              <Tooltip title="Abrir ficha de la orden de pago">
                                <Button
                                  size="small"
                                  startIcon={
                                    <OpenInNewIcon fontSize="small" />
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/ordenes_pago_proveedores/${opId}`
                                    );
                                  }}
                                >
                                  OP
                                </Button>
                              </Tooltip>
                            ) : null}
                          </Grid>
                        </Grid>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TablaComprobantesProveedor
                          comprobantes={prov.comprobantes}
                        />
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              gutterBottom
                            >
                              Retenciones
                            </Typography>
                            {retenciones.length === 0 ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Sin retenciones.
                              </Typography>
                            ) : (
                              <Stack spacing={0.25}>
                                {retenciones.map((linea) => (
                                  <Stack
                                    key={linea.key}
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={1}
                                  >
                                    <Typography variant="body2">
                                      {linea.label}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {formatMoneda(linea.importe)}
                                    </Typography>
                                  </Stack>
                                ))}
                                <Divider sx={{ my: 0.5 }} />
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    Total retenido
                                  </Typography>
                                  <Typography variant="body2" fontWeight={700}>
                                    {formatMoneda(prov.totales?.retenciones)}
                                  </Typography>
                                </Stack>
                              </Stack>
                            )}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              gutterBottom
                            >
                              Medios de pago
                            </Typography>
                            <MediosPagoProveedor medios={prov.mediosPago} />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}

            {pagosNoProveedor.length > 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, bgcolor: "#fff" }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    Pagos varios
                  </Typography>
                  <Chip
                    size="small"
                    color="secondary"
                    label={`${pagosNoProveedor.length} ítem${
                      pagosNoProveedor.length === 1 ? "" : "s"
                    }`}
                  />
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#2471A3" }}>
                        {["Concepto", "Motivo", "Fecha", "Monto", "Estado"].map(
                          (h) => (
                            <TableCell
                              key={h}
                              sx={{ color: "#fff", fontWeight: 500 }}
                              align={h === "Monto" ? "right" : "left"}
                            >
                              {h}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagosNoProveedor.map((pago) => (
                        <TableRow
                          key={
                            pago.id ??
                            pago.id_pago_no_proveedor ??
                            `${pago.concepto}-${pago.monto}`
                          }
                          hover
                        >
                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <span>
                                {pago.concepto || pago.tipo || "—"}
                              </span>
                              <Chip
                                size="small"
                                color="secondary"
                                label="Pago varios"
                                sx={{ height: 20 }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {pago.motivo || pago.descripcion || "—"}
                          </TableCell>
                          <TableCell>
                            {formatearFecha(pago.fecha || pago.fecha_pago)}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoneda(
                              pago.importe_a_pagar ??
                                pago.monto ??
                                pago.importe
                            )}
                          </TableCell>
                          <TableCell>
                            {pago.estado ? (
                              <Chip
                                size="small"
                                label={String(pago.estado)}
                                variant="outlined"
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : null}

            {proveedores.length === 0 && pagosNoProveedor.length === 0 ? (
              <Alert severity="info">Este lote no tiene ítems.</Alert>
            ) : null}

            {!loading && detalle && (puedeAprobar || puedeAnular) ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: "flex-end",
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                {puedeAnular ? (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<BlockIcon />}
                    onClick={() => setModalAnular(true)}
                    disabled={aprobando || anulando}
                  >
                    Anular lote
                  </Button>
                ) : null}
                {puedeAprobar ? (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => setDialogAprobar(true)}
                    disabled={aprobando || anulando}
                  >
                    Aprobar lote
                  </Button>
                ) : null}
              </Box>
            ) : null}
          </>
        ) : null}
      </Container>

      <DialogAprobarLote
        open={dialogAprobar}
        onClose={() => {
          if (!aprobando) setDialogAprobar(false);
        }}
        onConfirm={handleAprobar}
        aprobando={aprobando}
        nroLote={lote.nro_lote}
        cantidadOrdenes={proveedores.length}
        cantidadPagosVarios={pagosNoProveedor.length}
        totalNeto={totales?.neto}
      />

      <ModalAnularOrdenPago
        open={modalAnular}
        onClose={() => {
          if (!anulando) setModalAnular(false);
        }}
        onConfirm={handleAnular}
        nroOp={lote.nro_lote || ""}
        anulando={anulando}
        titulo="Anular lote"
        advertencia={
          loteSoloPagosVarios
            ? "Se anula el lote y los pagos varios vuelven a quedar disponibles. ¿Continuar?"
            : "Se anulan todas las órdenes de pago del lote y los comprobantes vuelven a quedar disponibles. ¿Continuar?"
        }
        detalle="Esta acción no se puede deshacer. El lote pasará a estado Anulado."
      />
    </>
  );
}
